// Personal-navigation layout logic (PRIME-NAV-UX-001) — pure, dependency-light,
// exhaustively unit-tested. This module owns the persisted schema: normalize /
// validate, RBAC filtering + grant reconciliation, recommended-layout derivation,
// reorder/add/remove operations, Home protection, revision handling, and the
// encode/decode that reads/writes the layout through the preferences API.
//
// ── Persistence ───────────────────────────────────────────────────────────────
// The layout is stored as a first-class nested object under the reserved
// `navigation` preference key via the existing PATCH/GET /api/me/preferences.
// The API special-cases that key and validates it against the same locked v1
// schema (apps/api/src/routes/me.routes.js → validateNavigationPreference), so
// what we persist is exactly the schema object, and `navigation: null` clears
// it. (The coordinator authorized this narrow API surface; see WORKER_RESULT.md.)

import { rbac } from '@dha-nexus/shared';
import { isEssentialRoute } from '@dha-nexus/shared/navEssentials.js';
import {
  NAV_WORKSPACES,
  NAV_SECTION_ORDER,
  HOME_SECTION_KEY,
  HOME_ROUTE,
  getNavItem,
} from '../app/navRegistry.js';
import { normalizeCuratedEmoji } from '../features/me/navEmojiCatalog.js';

// Group-label rules shared by create/rename and the inline validator (AC-02).
export const MAX_GROUP_LABEL = 60;
export const CUSTOM_SECTION_SOURCE = 'custom';

export const SCHEMA_VERSION = 1;
export const NAV_PREF_KEY = 'navigation';
export const PROTECTED_ROUTES = Object.freeze([HOME_ROUTE]);

/* ───────────────────────────── identity / guards ───────────────────────────── */

export function isProtectedRoute(route) {
  return PROTECTED_ROUTES.includes(route);
}

export function isCustomLayout(schema) {
  return Boolean(
    schema &&
      typeof schema === 'object' &&
      schema.mode === 'custom' &&
      Array.isArray(schema.layout),
  );
}

function sectionIdFor(key) {
  return `sec-${String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')}`;
}

/* ───────────────────────────── normalization ───────────────────────────────── */

// Coerce an arbitrary parsed value into a valid page item, or null.
function normalizePageItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.type !== 'page') return null;
  if (typeof raw.route !== 'string' || !raw.route.startsWith('/')) return null;
  return { type: 'page', route: raw.route };
}

// Coerce a submenu item (PRIME-NAV-UX-002 forward-compat). 001 never emits these,
// but any that arrive from a future writer are preserved structurally so the
// schema carries 002 without migration.
function normalizeSubmenuItem(raw) {
  if (!raw || typeof raw !== 'object' || raw.type !== 'submenu') return null;
  return {
    type: 'submenu',
    id: typeof raw.id === 'string' && raw.id ? raw.id : freshId('sub'),
    label: typeof raw.label === 'string' ? raw.label : null,
    // Curated emoji only (decision 4): a value off the client allowlist is
    // dropped to null on load, so a persisted/tampered non-catalog value never
    // renders. Submenus nest exactly one level — children must be pages.
    emoji: normalizeCuratedEmoji(raw.emoji),
    collapsed: raw.collapsed === true,
    items: Array.isArray(raw.items) ? raw.items.map(normalizePageItem).filter(Boolean) : [],
  };
}

function normalizeSectionItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.type === 'submenu') return normalizeSubmenuItem(raw);
  return normalizePageItem(raw);
}

function normalizeSection(raw, index) {
  if (!raw || typeof raw !== 'object') return null;
  const source = typeof raw.source === 'string' && raw.source ? raw.source : 'custom';
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : `${sectionIdFor(source)}-${index}`,
    type: 'section',
    source,
    label: typeof raw.label === 'string' ? raw.label : null,
    emoji: normalizeCuratedEmoji(raw.emoji),
    collapsed: raw.collapsed === true,
    items: Array.isArray(raw.items) ? raw.items.map(normalizeSectionItem).filter(Boolean) : [],
  };
}

// Deduplicate routes across the WHOLE layout — a route may appear only once,
// first occurrence wins (ticket req 4 + 001 hardening, decision 7). Mutates the
// freshly-built layout array (only ever called on normalizeSchema output).
function dedupeRoutes(layout) {
  const seen = new Set();
  for (const section of layout) {
    const kept = [];
    for (const item of section.items) {
      if (item.type === 'page') {
        if (seen.has(item.route)) continue;
        seen.add(item.route);
        kept.push(item);
      } else if (item.type === 'submenu') {
        item.items = item.items.filter((p) => {
          if (seen.has(p.route)) return false;
          seen.add(p.route);
          return true;
        });
        kept.push(item);
      }
    }
    section.items = kept;
  }
  return layout;
}

/**
 * Normalize a raw parsed value into a valid schema object, or null when it is
 * absent / not a custom layout / structurally unusable (→ caller falls back to
 * the default rail). Forward-compatible 002 fields are preserved.
 */
export function normalizeSchema(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (raw.mode !== 'custom') return null;
  if (raw.version !== SCHEMA_VERSION) return null;
  if (!Array.isArray(raw.layout)) return null;
  const revision =
    Number.isInteger(raw.revision) && raw.revision >= 0 ? raw.revision : 0;
  const layout = dedupeRoutes(raw.layout.map(normalizeSection).filter(Boolean));
  return { version: SCHEMA_VERSION, revision, mode: 'custom', layout };
}

// Fresh, server-safe id (matches me.routes NAV_ID_PATTERN: alnum start, ≤ 40).
function freshId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

/* ───────────────────────────── encode / decode ─────────────────────────────── */

/**
 * Encode a schema into the `{ navigation }` preference payload for PATCH/PUT.
 * A null/absent custom layout encodes to `{ navigation: null }` (→ clears it).
 * The layout is run through normalizeSchema so the persisted object carries
 * exactly the known, server-validated fields (no stray properties).
 */
export function encodeNavPreferences(schema) {
  if (!isCustomLayout(schema)) return { [NAV_PREF_KEY]: null };
  const clean = normalizeSchema({
    version: SCHEMA_VERSION,
    revision: Number.isInteger(schema.revision) && schema.revision >= 0 ? schema.revision : 0,
    mode: 'custom',
    layout: schema.layout,
  });
  return { [NAV_PREF_KEY]: clean };
}

/**
 * Decode a persisted preferences object (the `.data` payload from
 * GET /me/preferences) back into a schema, or null when no custom layout is
 * stored (absent / null / structurally unusable).
 */
export function decodeNavPreferences(prefs) {
  const raw = prefs && typeof prefs === 'object' ? prefs[NAV_PREF_KEY] : null;
  if (!raw) return null;
  return normalizeSchema(raw);
}

/** Read just the stored revision (for conflict detection). null = no layout. */
export function readRevision(prefs) {
  const schema = decodeNavPreferences(prefs);
  return schema ? schema.revision : null;
}

/* ───────────────────────────── registry helpers ────────────────────────────── */

// The set of canonical routes the editor may ever offer, with owning section.
function canonicalRoutes() {
  const rows = [];
  for (const workspace of NAV_WORKSPACES) {
    for (const item of workspace.items) {
      rows.push({ route: item.to, section: workspace.label });
    }
  }
  return rows;
}

function routesInSchema(schema) {
  const set = new Set();
  if (!isCustomLayout(schema)) return set;
  for (const section of schema.layout) {
    for (const item of section.items || []) {
      if (item.type === 'page') set.add(item.route);
      else if (item.type === 'submenu') {
        for (const sub of item.items || []) if (sub.type === 'page') set.add(sub.route);
      }
    }
  }
  return set;
}

/* ───────────────────────────── RBAC + reconciliation ───────────────────────── */

function filterItemsByRbac(items, roles) {
  const out = [];
  for (const item of items || []) {
    if (item.type === 'page') {
      if (getNavItem(item.route) && rbac.canSeeRoute(item.route, roles)) out.push(item);
    } else if (item.type === 'submenu') {
      const subItems = filterItemsByRbac(item.items, roles);
      if (subItems.length) out.push({ ...item, items: subItems });
    }
  }
  return out;
}

/**
 * Re-apply RBAC to a schema: drop pages that are unknown to the registry or that
 * the roles may no longer see (revoked-grant reconciliation, AC-08). Preferences
 * can only ever HIDE — this never grants a route. With `keepEmptySections` the
 * section headers survive for the editor; without it, empty sections are dropped
 * for rail rendering (except Home is always retained with a synthesized /home).
 */
export function applyRbacToLayout(schema, roles, { keepEmptySections = false } = {}) {
  if (!isCustomLayout(schema)) return null;
  const layout = [];
  let sawHome = false;
  for (const section of schema.layout) {
    const items = filterItemsByRbac(section.items, roles);
    const isHome = section.source === HOME_SECTION_KEY;
    if (isHome) {
      sawHome = true;
      // Home protection: /home is always present and always first.
      const hasHome = items.some((i) => i.type === 'page' && i.route === HOME_ROUTE);
      const withHome = hasHome
        ? [
            ...items.filter((i) => i.type === 'page' && i.route === HOME_ROUTE),
            ...items.filter((i) => !(i.type === 'page' && i.route === HOME_ROUTE)),
          ]
        : [{ type: 'page', route: HOME_ROUTE }, ...items];
      layout.push({ ...section, items: withHome });
      continue;
    }
    if (items.length || keepEmptySections) layout.push({ ...section, items });
  }
  // If the saved layout somehow lacked a Home section, synthesize a minimal one
  // so Mission Brief is never lost (decision 3).
  if (!sawHome) {
    layout.unshift({
      id: sectionIdFor(HOME_SECTION_KEY),
      type: 'section',
      source: HOME_SECTION_KEY,
      label: null,
      emoji: null,
      collapsed: false,
      items: [{ type: 'page', route: HOME_ROUTE }],
    });
  }
  return { ...schema, layout };
}

/* ───────────────────────────── render model ────────────────────────────────── */

/**
 * Resolve a schema into the concrete render model both the real rail and the
 * editor's live preview consume: [{ key, label, emoji, items:[{route,label,icon}] }].
 * Returns null when there is no custom layout (caller renders the default rail).
 */
export function resolveRailModel({ schema, roles }) {
  if (!isCustomLayout(schema)) return null;
  const filtered = applyRbacToLayout(schema, roles, { keepEmptySections: false });
  const sections = [];
  for (const section of filtered.layout) {
    const items = [];
    for (const item of section.items || []) {
      if (item.type === 'page') {
        const meta = getNavItem(item.route);
        if (!meta) continue;
        items.push({ type: 'page', route: item.route, label: meta.label, icon: meta.icon });
      } else if (item.type === 'submenu') {
        const subItems = [];
        for (const page of item.items || []) {
          const meta = getNavItem(page.route);
          if (!meta) continue;
          subItems.push({ type: 'page', route: page.route, label: meta.label, icon: meta.icon });
        }
        if (!subItems.length) continue; // empty submenu after RBAC re-filter → hide
        items.push({
          type: 'submenu',
          id: item.id,
          label: item.label || 'Group',
          emoji: item.emoji || null,
          collapsed: item.collapsed === true,
          items: subItems,
        });
      }
    }
    if (!items.length) continue;
    sections.push({
      // `key` stays the canonical source (back-compat with 001 consumers); `id`
      // is the stable, per-section unique React key (custom sections all share
      // source 'custom', so consumers must key on id).
      key: section.source,
      id: section.id,
      source: section.source,
      label: section.label || section.source,
      emoji: section.emoji || null,
      collapsed: section.collapsed === true,
      items,
    });
  }
  return sections;
}

/* ───────────────────────────── recommended / available ─────────────────────── */

/**
 * Derive the recommended ("persona default") layout: the canonical sections and
 * pages the persona sees today, with the essentials set SELECTED (decision 6).
 * This is what Reset-to-recommendation restores in the editor.
 */
export function deriveRecommendedLayout(roles) {
  const layout = [];
  for (const workspace of NAV_WORKSPACES) {
    const items = [];
    for (const item of workspace.items) {
      if (!rbac.canSeeRoute(item.to, roles)) continue;
      if (workspace.label === HOME_SECTION_KEY && item.to === HOME_ROUTE) {
        items.unshift({ type: 'page', route: item.to });
        continue;
      }
      if (isEssentialRoute(item.to)) items.push({ type: 'page', route: item.to });
    }
    if (workspace.label === HOME_SECTION_KEY && !items.some((i) => i.route === HOME_ROUTE)) {
      items.unshift({ type: 'page', route: HOME_ROUTE });
    }
    if (!items.length) continue;
    layout.push({
      id: sectionIdFor(workspace.label),
      type: 'section',
      source: workspace.label,
      label: null,
      emoji: null,
      collapsed: false,
      items,
    });
  }
  return { version: SCHEMA_VERSION, revision: 0, mode: 'custom', layout };
}

/**
 * Available pages = every RBAC-visible canonical route NOT already in the layout,
 * grouped by owning section in canonical order. Newly granted routes appear here
 * automatically (AC-08); unauthorized routes are never listed (AC-02).
 */
export function deriveAvailablePages({ schema, roles }) {
  const present = routesInSchema(schema);
  const bySection = new Map();
  for (const { route, section } of canonicalRoutes()) {
    if (present.has(route)) continue;
    if (!rbac.canSeeRoute(route, roles)) continue;
    const meta = getNavItem(route);
    if (!meta) continue;
    if (!bySection.has(section)) bySection.set(section, []);
    bySection.get(section).push({ route, label: meta.label, icon: meta.icon });
  }
  return NAV_SECTION_ORDER.filter((key) => bySection.has(key)).map((key) => ({
    key,
    label: key,
    routes: bySection.get(key),
  }));
}

/* ───────────────────────────── edit operations ─────────────────────────────── */

// All edit ops are immutable: they return a fresh schema and never mutate input.
function cloneLayout(schema) {
  return schema.layout.map((s) => ({
    ...s,
    items: s.items.map((i) =>
      i.type === 'submenu' ? { ...i, items: i.items.map((p) => ({ ...p })) } : { ...i },
    ),
  }));
}

/** Add a canonical route as a page into its owning section (creating it if needed). */
export function addPage(schema, route) {
  if (!isCustomLayout(schema)) return schema;
  const meta = getNavItem(route);
  if (!meta) return schema;
  if (routesInSchema(schema).has(route)) return schema;
  const targetKey = meta.section;
  const layout = cloneLayout(schema);
  const existing = layout.find((s) => s.source === targetKey);
  if (existing) {
    existing.items.push({ type: 'page', route });
  } else {
    const newSection = {
      id: sectionIdFor(targetKey),
      type: 'section',
      source: targetKey,
      label: null,
      emoji: null,
      collapsed: false,
      items: [{ type: 'page', route }],
    };
    // Insert in canonical section order.
    const targetRank = NAV_SECTION_ORDER.indexOf(targetKey);
    let insertAt = layout.length;
    for (let i = 0; i < layout.length; i += 1) {
      if (NAV_SECTION_ORDER.indexOf(layout[i].source) > targetRank) {
        insertAt = i;
        break;
      }
    }
    layout.splice(insertAt, 0, newSection);
  }
  return { ...schema, layout };
}

/** Remove a page unless it is protected. Empty non-Home sections are dropped. */
export function removePage(schema, route) {
  if (!isCustomLayout(schema)) return schema;
  if (isProtectedRoute(route)) return schema;
  let layout = cloneLayout(schema);
  for (const section of layout) {
    section.items = section.items.flatMap((item) => {
      if (item.type === 'page') return item.route === route ? [] : [item];
      const items = item.items.filter((page) => page.route !== route);
      return items.length ? [{ ...item, items }] : [];
    });
  }
  layout = layout.filter((s) => s.source === HOME_SECTION_KEY || s.items.length > 0);
  return { ...schema, layout };
}

/** Reorder a page or submenu by its absolute position within section.items. */
export function reorderSectionItem(schema, sectionId, fromIndex, toIndex) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const section = layout.find((s) => s.id === sectionId);
  if (!section) return schema;
  const { items } = section;
  if (
    !Number.isInteger(fromIndex) ||
    !Number.isInteger(toIndex) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return schema;
  }
  if (section.source === HOME_SECTION_KEY) {
    const homeIndex = items.findIndex(
      (item) => item.type === 'page' && item.route === HOME_ROUTE,
    );
    if (homeIndex === 0 && (fromIndex === 0 || toIndex === 0)) return schema;
  }
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);
  return { ...schema, layout };
}

/**
 * Reorder a page within its section. `fromIndex`/`toIndex` are indices among the
 * section's PAGE items only (submenus keep their positions) — 002 sections can
 * interleave pages and submenus, so page-relative indices are what the editor's
 * page list emits. For an all-pages section this is identical to 001 behavior.
 */
export function reorderPageWithinSection(schema, sectionId, fromIndex, toIndex) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const section = layout.find((s) => s.id === sectionId);
  if (!section) return schema;
  const { items } = section;
  // Map page-relative indices to absolute positions within section.items.
  const pagePositions = [];
  items.forEach((it, idx) => {
    if (it.type === 'page') pagePositions.push(idx);
  });
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= pagePositions.length ||
    toIndex >= pagePositions.length ||
    fromIndex === toIndex
  ) {
    return schema;
  }
  // Home protection: /home is pinned to page 0 of the Home section.
  if (section.source === HOME_SECTION_KEY) {
    const homePageIdx = pagePositions.findIndex(
      (pos) => items[pos].type === 'page' && items[pos].route === HOME_ROUTE,
    );
    if (homePageIdx === 0 && (fromIndex === 0 || toIndex === 0)) return schema;
  }
  const realFrom = pagePositions[fromIndex];
  const [moved] = items.splice(realFrom, 1);
  // Recompute the target absolute position after removal.
  const remaining = [];
  items.forEach((it, idx) => {
    if (it.type === 'page') remaining.push(idx);
  });
  const realTo = toIndex >= remaining.length ? items.length : remaining[toIndex];
  items.splice(realTo, 0, moved);
  return { ...schema, layout };
}

/** Reorder whole sections. */
export function moveSection(schema, fromIndex, toIndex) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= layout.length ||
    toIndex >= layout.length ||
    fromIndex === toIndex
  ) {
    return schema;
  }
  const [moved] = layout.splice(fromIndex, 1);
  layout.splice(toIndex, 0, moved);
  return { ...schema, layout };
}

/** Add every available authorized page (select-all). */
export function selectAll(schema, roles) {
  let next = schema;
  for (const group of deriveAvailablePages({ schema, roles })) {
    for (const { route } of group.routes) next = addPage(next, route);
  }
  return next;
}

/** Remove every optional (non-protected) page, leaving only Mission Brief. */
export function clearOptional(schema) {
  if (!isCustomLayout(schema)) return schema;
  const present = [...routesInSchema(schema)];
  let next = schema;
  for (const route of present) {
    if (!isProtectedRoute(route)) next = removePage(next, route);
  }
  return next;
}

/** Increment the revision (called at save time). */
export function withBumpedRevision(schema) {
  return {
    ...schema,
    revision: (Number.isInteger(schema?.revision) && schema.revision >= 0 ? schema.revision : 0) + 1,
  };
}

/* ───────────────── PRIME-NAV-UX-002: sections / submenus / collapse ─────────── */

function findSection(layout, sectionId) {
  return layout.find((s) => s.id === sectionId) || null;
}

function findSubmenu(section, submenuId) {
  if (!section) return null;
  return section.items.find((i) => i.type === 'submenu' && i.id === submenuId) || null;
}

export function isHomeSection(section) {
  return Boolean(section && section.source === HOME_SECTION_KEY);
}

function cleanLabel(label) {
  const trimmed = typeof label === 'string' ? label.trim() : '';
  if (!trimmed) return null;
  return trimmed.slice(0, MAX_GROUP_LABEL);
}

/** Create a new personal (custom) section with a required user label. */
export function createSection(schema, label) {
  if (!isCustomLayout(schema)) return schema;
  const clean = cleanLabel(label);
  if (!clean) return schema;
  const layout = cloneLayout(schema);
  layout.push({
    id: freshId('sec'),
    type: 'section',
    source: CUSTOM_SECTION_SOURCE,
    label: clean,
    emoji: null,
    collapsed: false,
    items: [],
  });
  return { ...schema, layout };
}

/**
 * Rename a non-Home section. `label === null` restores the canonical name
 * (label override cleared). A blank/whitespace label is refused (no-op) — the
 * inline validator (validateSectionLabel) is the predictable UI guard (AC-02).
 */
export function renameSection(schema, sectionId, label) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const section = findSection(layout, sectionId);
  if (!section || isHomeSection(section)) return schema; // Home fixed (AC-01)
  if (label === null) {
    section.label = null;
  } else {
    const clean = cleanLabel(label);
    if (!clean) return schema;
    section.label = clean;
  }
  return { ...schema, layout };
}

/** Set (curated) or clear a section emoji. Home takes no emoji (AC-01). */
export function setSectionEmoji(schema, sectionId, emoji) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const section = findSection(layout, sectionId);
  if (!section || isHomeSection(section)) return schema;
  section.emoji = normalizeCuratedEmoji(emoji);
  return { ...schema, layout };
}

/**
 * Delete a CUSTOM section. Its pages/submenus are dropped from the layout, so
 * every route it held returns to Available (becomes unselected). Canonical
 * sections (source !== 'custom') and Home are never deletable.
 */
export function deleteSection(schema, sectionId) {
  if (!isCustomLayout(schema)) return schema;
  const section = findSection(schema.layout, sectionId);
  if (!section || section.source !== CUSTOM_SECTION_SOURCE) return schema;
  const layout = cloneLayout(schema).filter((s) => s.id !== sectionId);
  return { ...schema, layout };
}

export function setSectionCollapsed(schema, sectionId, collapsed) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const section = findSection(layout, sectionId);
  if (!section) return schema;
  section.collapsed = Boolean(collapsed);
  return { ...schema, layout };
}

export function toggleSectionCollapsed(schema, sectionId) {
  const section = isCustomLayout(schema) ? findSection(schema.layout, sectionId) : null;
  if (!section) return schema;
  return setSectionCollapsed(schema, sectionId, !section.collapsed);
}

/** Create a one-level submenu inside a section (children are pages only). */
export function createSubmenu(schema, sectionId, label) {
  if (!isCustomLayout(schema)) return schema;
  const clean = cleanLabel(label);
  if (!clean) return schema;
  const layout = cloneLayout(schema);
  const section = findSection(layout, sectionId);
  if (!section) return schema;
  section.items.push({
    type: 'submenu',
    id: freshId('sub'),
    label: clean,
    emoji: null,
    collapsed: false,
    items: [],
  });
  return { ...schema, layout };
}

export function renameSubmenu(schema, sectionId, submenuId, label) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const submenu = findSubmenu(findSection(layout, sectionId), submenuId);
  if (!submenu) return schema;
  if (label === null) {
    submenu.label = null;
  } else {
    const clean = cleanLabel(label);
    if (!clean) return schema;
    submenu.label = clean;
  }
  return { ...schema, layout };
}

export function setSubmenuEmoji(schema, sectionId, submenuId, emoji) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const submenu = findSubmenu(findSection(layout, sectionId), submenuId);
  if (!submenu) return schema;
  submenu.emoji = normalizeCuratedEmoji(emoji);
  return { ...schema, layout };
}

export function setSubmenuCollapsed(schema, sectionId, submenuId, collapsed) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const submenu = findSubmenu(findSection(layout, sectionId), submenuId);
  if (!submenu) return schema;
  submenu.collapsed = Boolean(collapsed);
  return { ...schema, layout };
}

export function toggleSubmenuCollapsed(schema, sectionId, submenuId) {
  const submenu = isCustomLayout(schema)
    ? findSubmenu(findSection(schema.layout, sectionId), submenuId)
    : null;
  if (!submenu) return schema;
  return setSubmenuCollapsed(schema, sectionId, submenuId, !submenu.collapsed);
}

/** Delete a submenu; its pages return to Available (become unselected). */
export function deleteSubmenu(schema, sectionId, submenuId) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const section = findSection(layout, sectionId);
  if (!section) return schema;
  const before = section.items.length;
  section.items = section.items.filter((i) => !(i.type === 'submenu' && i.id === submenuId));
  if (section.items.length === before) return schema;
  return { ...schema, layout };
}

/** Reorder a page inside a submenu. */
export function reorderPageWithinSubmenu(schema, sectionId, submenuId, fromIndex, toIndex) {
  if (!isCustomLayout(schema)) return schema;
  const layout = cloneLayout(schema);
  const submenu = findSubmenu(findSection(layout, sectionId), submenuId);
  if (!submenu) return schema;
  const { items } = submenu;
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return schema;
  }
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);
  return { ...schema, layout };
}

// Remove a route from wherever it currently lives (section body or a submenu).
// Returns true if it was found+removed. Never drops the containing section.
function detachRoute(layout, route) {
  for (const section of layout) {
    const idx = section.items.findIndex((i) => i.type === 'page' && i.route === route);
    if (idx >= 0) {
      section.items.splice(idx, 1);
      return true;
    }
    for (const item of section.items) {
      if (item.type === 'submenu') {
        const j = item.items.findIndex((p) => p.route === route);
        if (j >= 0) {
          item.items.splice(j, 1);
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Move (or add) a known page into a target container — a section body or a
 * submenu — preserving cross-layout route uniqueness by construction: the page
 * is detached from any prior location, then appended to the target. This is the
 * one relocation primitive the editor uses for BOTH "move between groups" and
 * "add an available page into a specific group". /home is fixed (AC-01) and the
 * one-level limit holds because a submenu target only ever receives pages.
 *
 * @param {object} target `{ kind: 'section'|'submenu', sectionId, submenuId? }`
 */
export function movePage(schema, route, target) {
  if (!isCustomLayout(schema)) return schema;
  if (isProtectedRoute(route)) return schema; // /home never moves (AC-01)
  if (!getNavItem(route)) return schema; // unknown route
  if (!target || typeof target !== 'object') return schema;
  const layout = cloneLayout(schema);
  const section = findSection(layout, target.sectionId);
  if (!section) return schema;
  let container;
  if (target.kind === 'submenu') {
    const submenu = findSubmenu(section, target.submenuId);
    if (!submenu) return schema;
    container = submenu.items;
  } else if (target.kind === 'section') {
    container = section.items;
  } else {
    return schema;
  }
  detachRoute(layout, route); // ok if absent — this doubles as "add"
  container.push({ type: 'page', route });
  return { ...schema, layout };
}

/* ───────────────────────── label validation (AC-02) ────────────────────────── */

function siblingGroupLabels(schema, sectionId, submenuId) {
  if (!isCustomLayout(schema)) return [];
  if (submenuId) {
    const section = findSection(schema.layout, sectionId);
    if (!section) return [];
    return section.items
      .filter((i) => i.type === 'submenu' && i.id !== submenuId)
      .map((i) => i.label || '');
  }
  return schema.layout.filter((s) => s.id !== sectionId).map((s) => s.label || s.source);
}

/**
 * Validate a proposed group (section or submenu) label. Predictable + accessible
 * (decision 3): trimmed, non-empty, ≤ 60 chars, unique among sibling groups
 * case-insensitively (self excluded). Returns `{ ok, error, value }` where
 * `value` is the trimmed candidate and `error` is user-facing copy on failure.
 */
function validateGroupLabel(schema, { sectionId, submenuId, rawLabel }) {
  const value = typeof rawLabel === 'string' ? rawLabel.trim() : '';
  if (!value) return { ok: false, error: 'Enter a name.', value: '' };
  if (value.length > MAX_GROUP_LABEL) {
    return { ok: false, error: `Use ${MAX_GROUP_LABEL} characters or fewer.`, value };
  }
  const siblings = siblingGroupLabels(schema, sectionId, submenuId);
  if (siblings.some((label) => label.toLowerCase() === value.toLowerCase())) {
    return { ok: false, error: 'Another group already uses that name.', value };
  }
  return { ok: true, error: null, value };
}

export function validateSectionLabel(schema, sectionId, rawLabel) {
  return validateGroupLabel(schema, { sectionId, submenuId: null, rawLabel });
}

export function validateSubmenuLabel(schema, sectionId, submenuId, rawLabel) {
  return validateGroupLabel(schema, { sectionId, submenuId, rawLabel });
}
